import type { IWorkJourneyMatrixCell } from '@src/models/interfaces/workJourneyManagement.interface';
import { normalizeDaysOfWeek } from '@src/packages/utils/dates';

import { isFutureMatrixCalendarDate } from './isFutureMatrixCalendarDate';

/**
 * ISO weekday 1=Mon … 7=Sun for a calendar `yyyy-MM-dd` (civil date, not a timestamp).
 * Matches Luxon `DateTime.weekday` / workShift.daysOfWeek.
 */
export const getIsoWeekdayFromIsoDate = (isoDate: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || !month || !day) return null;
  const utcNoon = new Date(Date.UTC(year, month - 1, day, 12));
  if (Number.isNaN(utcNoon.getTime())) return null;
  const jsDay = utcNoon.getUTCDay(); // 0=Sun … 6=Sat
  return jsDay === 0 ? 7 : jsDay;
};

/**
 * Same rule as Services `isScheduledWorkDay`: empty `daysOfWeek` means every day works.
 */
export const isMatrixScheduledWorkDay = (
  isoDate: string,
  daysOfWeek?: Array<string | number> | null,
): boolean => {
  const scheduled = normalizeDaysOfWeek(daysOfWeek);
  if (scheduled.length === 0) return true;
  const weekday = getIsoWeekdayFromIsoDate(isoDate);
  if (weekday == null) return true;
  return scheduled.includes(weekday);
};

/**
 * Same gate as the blank-cell click handler: daysOfWeek loaded, not future,
 * and a scheduled work day for this journey.
 */
export const canCreateBlankPastDayAttendance = (
  isoDate: string,
  daysOfWeek?: Array<string | number> | null,
): boolean => {
  if (!isoDate || daysOfWeek == null) return false;
  if (isFutureMatrixCalendarDate(isoDate)) return false;
  return isMatrixScheduledWorkDay(isoDate, daysOfWeek);
};

/**
 * Rest-day dates for the open journey month.
 * Prefers `workShift.daysOfWeek`; also unions dates already painted as rest/D
 * (covers older API payloads without daysOfWeek).
 */
export const buildMatrixRestDateSet = (
  days: string[],
  cells: IWorkJourneyMatrixCell[],
  daysOfWeek?: Array<string | number> | null,
): Set<string> => {
  const restDates = new Set<string>();

  cells.forEach(cell => {
    if (cell.kind === 'rest' || cell.code === 'D') {
      restDates.add(cell.date);
    }
  });

  const scheduled = normalizeDaysOfWeek(daysOfWeek);
  if (scheduled.length > 0) {
    days.forEach(date => {
      if (!isMatrixScheduledWorkDay(date, scheduled)) {
        restDates.add(date);
      }
    });
  }

  return restDates;
};
